import { SEO } from "@/components/SEO";
import { CardMark } from "@/components/card/CardChrome";
import apiClient from "@/services/api/apiClient";
import { useAuthStore } from "@/stores/authStore";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { takeFirstRunAfterPayment } from "@/utils/firstRunAfterPayment";
import confetti from "canvas-confetti";
import { CheckCircle2, Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

/**
 * Where Stripe returns a buyer (/payment-success?session_id=…).
 *
 * The page used to say "Your 14-day free trial starts now" to everyone —
 * single-trip buyers and people with no trial left included — read the
 * subscription once after three seconds and reload into Home after five.
 * When the webhook was slower, a paying customer landed on FREE. It now asks
 * the backend to confirm this checkout (GET /stripe/checkout-session/:id,
 * which applies it if the webhook has not), waits while the payment is on its
 * way, and says what was actually bought.
 */

interface Confirmation {
  state: "applied" | "pending" | "failed";
  kind: "subscription" | "single_trip";
  tier: string | null;
  trialEnd: string | null;
  accessUntil: string | null;
}

const POLL_MS = 2000;
const MAX_POLLS = 20;

function planName(tier: string | null): string {
  if (!tier) return "HealthPass";
  return tier.startsWith("PREMIUM") ? "PREMIUM" : "PLUS";
}

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { token } = useAuthStore();
  const { fetchSubscription } = useSubscriptionStore();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [result, setResult] = useState<Confirmation | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [next] = useState(() =>
    takeFirstRunAfterPayment() ? "/traveler/welcome" : "/dashboard",
  );
  const celebrated = useRef(false);

  useEffect(() => {
    if (!sessionId || !token) return;
    let cancelled = false;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;

    const ask = async () => {
      try {
        const res = await apiClient.get(
          `/stripe/checkout-session/${encodeURIComponent(sessionId)}`,
        );
        if (cancelled) return;
        const data = res.data.data as Confirmation;
        setResult(data);
        if (data.state === "applied") {
          void fetchSubscription(token);
          return;
        }
        if (data.state === "failed") return;
      } catch {
        // A hiccup while the webhook and this call race; ask again.
      }
      polls++;
      if (polls >= MAX_POLLS) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(ask, POLL_MS);
    };
    void ask();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, token, fetchSubscription]);

  useEffect(() => {
    if (result?.state !== "applied" || celebrated.current) return;
    celebrated.current = true;
    confetti({
      particleCount: 90,
      spread: 70,
      origin: { y: 0.55 },
      disableForReducedMotion: true,
    });
  }, [result?.state]);

  const date = (iso: string | null) => (iso ? fmt.date(iso, "long") : "");

  const applied = result?.state === "applied";
  const failed = result?.state === "failed" || !sessionId;
  const plan = planName(result?.tier ?? null);

  let title: string;
  let line: string;
  if (applied) {
    title = t.payWelcome.replace("{plan}", plan);
    line = result.trialEnd
      ? t.payTrialLine.replace("{date}", date(result.trialEnd))
      : result.accessUntil
        ? t.payTripLine.replace("{date}", date(result.accessUntil))
        : t.payActiveLine;
  } else if (failed) {
    title = t.payFailedTitle;
    line = t.payFailedLine;
  } else if (gaveUp) {
    title = t.payPendingTitle;
    line = t.payPendingLine;
  } else {
    title = t.payConfirming;
    line = t.payConfirmingLine;
  }

  return (
    <div className="min-h-screen bg-ground px-4 pb-12 pt-8 text-ink">
      <SEO title="HealthPass — payment" description={title} />
      <div className="mx-auto max-w-[480px]">
        <div className="flex items-center gap-2 text-primary-600">
          <CardMark className="h-[22px] w-[22px]" />
          <span className="font-display text-base font-bold tracking-[0.04em] text-ink">
            HEALTH PASS
          </span>
        </div>

        <section
          role="status"
          aria-live="polite"
          className="mt-8 rounded-[22px] bg-card p-6 text-center"
        >
          <div className="flex justify-center">
            {applied ? (
              <CheckCircle2
                className="h-14 w-14 text-health-700"
                aria-hidden="true"
              />
            ) : failed ? null : (
              <Clock
                className={`h-14 w-14 text-primary-600 ${gaveUp ? "" : "motion-safe:animate-pulse"}`}
                aria-hidden="true"
              />
            )}
          </div>
          <h1 className="mt-4 font-display text-[28px] font-extrabold leading-tight">
            {title}
          </h1>
          <p className="mt-2 text-[15px] leading-normal text-slate-600">
            {line}
          </p>

          {(applied || gaveUp) && (
            <Link
              to={next}
              className="mt-6 flex min-h-[52px] items-center justify-center rounded-2xl bg-ink px-4 text-[15px] font-bold text-white"
            >
              {next === "/traveler/welcome" ? t.payBuildCard : t.payContinue}
            </Link>
          )}
          {failed && (
            <Link
              to="/traveler/subscription/plans"
              className="mt-6 flex min-h-[52px] items-center justify-center rounded-2xl border border-input bg-card px-4 text-[15px] font-semibold"
            >
              {t.payBackToPlans}
            </Link>
          )}
        </section>
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
